import PropTypes from 'prop-types';
import React from 'react';
import LeadFormEmbed from '../../components/registry/LeadFormEmbed';
import { useTranslation } from '../../i18n/I18nContext';
import { config } from '../../config/runtimeConfig';
import './SmartFormDemo.css';

/**
 * Конфиг формы для витрины (#1334).
 *
 * `formMorphToChat` — ровно та фича, ради которой блок и существует: после
 * отправки форма не заканчивается баннером «спасибо», а передаёт разговор
 * ассистенту. Остальные поля заданы явно, чтобы витрина не зависела от
 * настроек организации: у демо своего form-template нет, и без списка полей
 * форма собралась бы из легаси-дефолтов.
 */
const buildFormAttrs = (t, own) => ({
  formMorphToChat: true,
  /* Форма лида собрана на СВОЁМ семействе токенов (`--sg-form-*`), отдельном
     от токенов витрины: радиус приходит из `appearance.cornerRadius` и
     пишется инлайном, поэтому CSS-переопределением его не достать. На витрине
     поля и кнопки — пилюли, а форма стояла на 8px и выпадала из строя.
     Правим штатным механизмом компонента, а не стилями: у дилеров значение
     по-прежнему своё, из их appearance-конфига. */
  formAppearance: { cornerRadius: 9999 },
  // Полный набор полей компонента (`DEFAULT_FORM_FIELDS` в `LeadFormEmbed`):
  // имя, телефон, почта, предпочтительный канал связи и сообщение. Витрина
  // показывала только имя и телефон — форма выглядела урезанной по сравнению
  // с тем, что дилер получает из коробки, и «умная форма» нечего было
  // показывать: выбор канала и свободный текст — это как раз то, что потом
  // подхватывает ассистент.
  //
  // `width: 'half'` — штатный механизм двухколоночной сетки формы (#1227):
  // короткие поля встают парами, длинные занимают строку целиком.
  formFieldList: [
    {
      key: 'name',
      type: 'text',
      enabled: true,
      required: true,
      label: t('showcase.form.name', 'Name'),
      ...(own && {
        placeholder: t('showcase.form.namePlaceholder', 'e.g. John Smith'),
      }),
      width: 'half',
    },
    {
      key: 'phone',
      type: 'tel',
      enabled: true,
      required: true,
      label: t('showcase.form.phone', 'Phone'),
      width: 'half',
    },
    {
      key: 'email',
      type: 'email',
      enabled: true,
      required: false,
      label: t('showcase.form.email', 'Email'),
      width: 'half',
    },
    {
      key: 'preferredChannel',
      type: 'channel',
      enabled: true,
      required: false,
      label: t('showcase.form.channel', 'How to reach you'),
      // Подписи вариантов канала — по языку витрины. Без `options` форма
      // берёт свои английские (так у дилеров).
      ...(own && {
        options: [
          { value: 'call', label: t('showcase.form.call', 'Call') },
          { value: 'whatsapp', label: 'WhatsApp' },
          { value: 'email', label: t('showcase.form.email', 'Email') },
        ],
      }),
      width: 'half',
    },
    {
      key: 'message',
      type: 'textarea',
      enabled: true,
      required: false,
      label: t('showcase.form.message', 'Message'),
      placeholder: t('showcase.form.messagePlaceholder', 'Which car are you after?'),
    },
  ],
  formTitle: t('showcase.form.title', 'Ask about a car'),
  // У дилера ключ как на main (`formSubmitText`): форма его не читает и
  // берёт свою подпись — так дилерская главная остаётся прежней.
  [own ? 'formSubmitLabel' : 'formSubmitText']: t('showcase.form.submit', 'Send'),
  formHandoverCtaText: t('showcase.form.handover', 'Continue with the assistant'),
});

/**
 * SmartFormDemo — «умная форма, которая превращается в чат» (#1334, план #1330).
 *
 * На витрине стоит НАСТОЯЩИЙ `LeadFormEmbed` из registry — тот же компонент,
 * что уезжает дилерам в режиме `mode: 'form'`. Рисовать имитацию формы, которая
 * «как бы» превращается в чат, на демо-показе бессмысленно: дилер её заполнит.
 *
 * Лежит в `pages/shared/`, а не в `components/`: `bootstrap.jsx` инлайнит в
 * Shadow DOM дилера глоб `components/**\/*.css` целиком, и обёртка витрины
 * иначе уехала бы в бандл каждому клиенту. Сам `LeadFormEmbed` живёт в
 * `components/registry/` и едет дилерам штатно — его мы не трогаем.
 */
export const SmartFormDemo = ({ showSteps = true, hideTitle = false }) => {
  const { t: translate } = useTranslation();
  // Витрина переводит форму и шаги; дилер по токену (`DealerHomePage`) видит
  // английский текст, как на main.
  const own = config.IS_OWN_STOCK;
  const t = own ? translate : (_key, fallback) => fallback;
  const FORM_ATTRS = buildFormAttrs(t, own);
  return (
  <div
    className={`smart-form-demo${
      showSteps ? '' : ' smart-form-demo--form-only'
    }`}
  >
    <div className="smart-form-demo__form">
      <LeadFormEmbed
        attrs={
          hideTitle
            ? {
                ...FORM_ATTRS,
                // Слияние, а не замена: иначе `hideTitle` стирал бы радиус.
                formAppearance: { ...FORM_ATTRS.formAppearance, hideTitle: true },
              }
            : FORM_ATTRS
        }
      />
    </div>
    {showSteps && (
      <ol className="smart-form-demo__steps">
        <li className="smart-form-demo__step">
          {t('showcase.form.step1', 'Buyer leaves a name and a phone — two fields, nothing else')}
        </li>
        <li className="smart-form-demo__step">
          {t('showcase.form.step2', 'The lead is in your CRM before the page reloads')}
        </li>
        <li className="smart-form-demo__step">
          {t(
            'showcase.form.step3',
            'Instead of a dead “thanks” screen the assistant picks the conversation up and keeps qualifying'
          )}
        </li>
      </ol>
    )}
  </div>
  );
};

SmartFormDemo.propTypes = {
  /**
   * Показывать список «что происходит после отправки». Нужен там, где блок
   * объясняет возможность. На CTA-полосе главной выключен: там над формой уже
   * стоят заголовок и подпись, и третий текст подряд превращает призыв к
   * действию в описание продукта.
   */
  showSteps: PropTypes.bool,
  /**
   * Скрыть собственный заголовок формы («Ask about a car»). Нужен там, где над
   * ней уже стоит заголовок блока: три заголовка подряд читаются как сбой
   * вёрстки. Прокидывается штатным `formAppearance.hideTitle` у `LeadFormEmbed`,
   * а не прячется стилями — иначе заголовок остался бы в разметке и его
   * озвучил бы скринридер.
   */
  hideTitle: PropTypes.bool,
};

export default SmartFormDemo;
